import type { CommandAction } from '../../types/CommandAction.js';
import type { UnlinkOptions } from './types/UnlinkOptions.js';
import { getSessionToLink } from '../link/getSessionToLink.js';
import { createCloudpackClient } from '@ms-cloudpack/api-server';
import { formatLinkSummary } from '../link/formatLinkSummary.js';
import { writeJson } from '@ms-cloudpack/json-utilities';
import path from 'path';

/**
 * Defines the "unlink" verb entry point.
 */
export const execute: CommandAction<UnlinkOptions> = async (params) => {
  // Use the current directory as the default package path.
  const { options, appPath, reporter } = params;
  const { logResolveMap, cachePath } = options;

  console.debug('Unlinking packages...');

  const sessionResult = await getSessionToLink({ reporter, cachePath });
  if (!sessionResult.session) {
    return sessionResult;
  }
  const { session } = sessionResult;
  const client = await createCloudpackClient({ url: session.urls.apiServer, reporter });

  const message = `Unlinking paths for ${session.projectName} at "${appPath}"`;
  const task = reporter.addTask(message);

  // Send request to api server to link paths.
  const { resolveMap, linkedPaths } = await client.unlinkPath.mutate({
    linkedPath: {
      path: appPath,
    },
  });
  await client.close();

  if (linkedPaths.some((p) => p.path === appPath)) {
    // Close down with an error.
    task.complete({ status: 'fail', message: `Failed to unlink paths.`, forceShow: true });
    return { hasErrors: true, message: 'Failed to unlink paths.' };
  }

  if (logResolveMap) {
    // Write the unlinked resolve map to disk.
    await writeJson(path.join(appPath, 'resolve-map-unlinked.json'), resolveMap);
  }

  // Close down successfully.
  task.complete({ status: 'complete', message: `Unlinked paths`, forceShow: true });
  const linkSummary = formatLinkSummary({ session, resolveMap, linkedPaths });

  return { message: linkSummary };
};
